import { useEffect, useRef } from "react";
import { authedFetch } from "@/lib/auth";
import { readSSEStream } from "@/lib/sse-reader";
import { trpc } from "@/lib/trpc";
import { handleAuthError } from "@/lib/trpc-error";
import type { ProjectLiveEvent } from "@iamu/shared";

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 15_000;

/**
 * Listens to live project SSE updates (/api/projects/:id/live) and invalidates
 * the relevant tRPC query caches on walkthrough pushes, completions, gate evaluations,
 * and finding triage actions.
 */
export function useProjectLiveUpdates(projectId: string | undefined) {
  const utils = trpc.useUtils();
  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);

  useEffect(() => {
    if (!projectId) return;

    let active = true;

    function cleanup() {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    function scheduleReconnect() {
      if (!active) return;
      if (reconnectTimerRef.current) return;

      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    }

    function handleEvent(event: ProjectLiveEvent) {
      switch (event.type) {
        case "walkthrough_pushed":
        case "walkthrough_completed":
          void utils.walkthrough.listByProject.invalidate({ projectId });
          void utils.walkthrough.getById.invalidate();
          void utils.finding.queueCounts.invalidate({ projectId });
          void utils.finding.queue.invalidate();
          void utils.gate.history.invalidate({ projectId });
          void utils.scenario.list.invalidate({ projectId });
          void utils.build.list.invalidate();
          void utils.activity.list.invalidate({ projectId });
          break;

        case "gate_evaluated":
          void utils.gate.history.invalidate({ projectId });
          void utils.build.list.invalidate();
          if (event.buildId) {
            void utils.build.get.invalidate({
              projectId,
              buildId: event.buildId,
            });
          }
          break;

        case "finding_triaged":
          void utils.finding.queueCounts.invalidate({ projectId });
          void utils.finding.queue.invalidate();
          void utils.finding.getById.invalidate();
          void utils.gate.history.invalidate({ projectId });
          void utils.activity.list.invalidate({ projectId });
          break;

        default:
          break;
      }
    }

    async function connect() {
      if (!active) return;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await authedFetch(`/api/projects/${projectId}/live`, {
          method: "GET",
          headers: {
            Accept: "text/event-stream",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError(new Error("Authentication required"));
            return;
          }
          if (response.status === 403 || response.status === 404) {
            // Unrecoverable authorization or deleted project; stop reconnecting
            return;
          }
          scheduleReconnect();
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          scheduleReconnect();
          return;
        }

        // Connection established, reset backoff delay
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;

        await readSSEStream<ProjectLiveEvent>(reader, (event) => {
          handleEvent(event);
        });

        // If stream ended cleanly or was dropped by proxy, schedule reconnect
        scheduleReconnect();
      } catch (err) {
        if ((err as Error).name === "AbortError" && !active) return;
        scheduleReconnect();
      }
    }

    connect();

    return () => {
      active = false;
      cleanup();
    };
  }, [projectId, utils]);
}
